import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

import dayjs, { type Dayjs } from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn"; // 导入中文语言包
import type { DeepCamelToSnake } from "@/types";

// 合并 tailwind classname
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);

const DEFAULT_LANGUAGE = "zh-cn"; // 默认语言为中文
const loadedLocales: { [key: string]: boolean } = {}; // 缓存已加载的语言包

export function setDateTimeLanguage(language: string) {
  language = language || DEFAULT_LANGUAGE;

  if (!loadedLocales[language]) {
    loadedLocales[language] = true;

    // 动态导入对应的语言包
    import(`dayjs/locale/${language}`).then((locale) => {
      dayjs.locale(locale.default);
    });
  } else {
    dayjs.locale(language);
  }
}
setDateTimeLanguage(DEFAULT_LANGUAGE);

export function getTimeAgoFromNow(date: string | number | Dayjs | Date | null | undefined): string {
  const now = dayjs();
  const targetDate = dayjs(date);

  return targetDate.from(now);
}

export function getCurrentTime(): string {
  return dayjs().format('YYYY-MM-DD HH:mm:ss');
}
export function formatTime(ms: number | string, formatter: string | undefined = 'YYYY-MM-DD HH:mm:ss'): string {
  return dayjs(ms).format(formatter);
}

/**
 * 数据对象key 驼峰下划线相互转化
 * @param {Object} data - 原始对象 支持-数组、key-value对象、字符串
 * @param {boolean} reverse false: SnakeToCamel-转驼峰 true: CamelToSnake-转下划线
 */
export function snakeToCamel<T extends { [key: string]: any } = {}, SnakeToCamel extends boolean = true>(
  data: SnakeToCamel extends true ? DeepCamelToSnake<T> : T,
  options: {
    reverse?: SnakeToCamel extends true ? false : true;
    modifier?: (value: (T | any), key?: string) => (T | any);
  } = {},
): SnakeToCamel extends true ? T : DeepCamelToSnake<T> {
  const {
    reverse = false,
    modifier = (value) => value,
  } = options;
  const formatTransferKey = (value: T | any, k?: string): any => {
    if (value instanceof Array) {
      return value.map(item => formatTransferKey(item));
    } else if (value instanceof Object) {
      const newValue: any = {};
      for (const key in value) {
        const keySnakeOrCamel = !reverse ? transferSnakeToCamel(key) : transferCamelToSnake(key);
        newValue[keySnakeOrCamel] = formatTransferKey(value[key], key);
      }
      return newValue;
    }
    else if (typeof value === 'string') {
      return value;
      // return !reverse ? transferSnakeToCamel(value) : transferCamelToSnake(value);
    } else if (typeof value === 'number' && (k?.endsWith('_id') || k === 'id')) {
      // 一统！对于所有ID转化成字符串格式
      return value.toString();
    }
    return modifier(value, k);
  };

  return formatTransferKey(data);
}

/**
 * 字符串下划线转驼峰
 * @param {String} value 需要转换的值
 */
export function transferSnakeToCamel(value: string) {
  return value.replace(/\_(\w)/g, (_, letter) => letter.toUpperCase())
}

/**
 * 字符串驼峰转下划线
 * @param {String} value 
 */
export function transferCamelToSnake(value: string) {
  return value.replace(/([a-z])([A-Z0-9])/g, '$1_$2').toLowerCase()
}


export function isEqual(value: any, other: any): boolean {
  if (value === other) {
    return true;
  }

  if (value === null || other === null) {
    return false;
  }

  if (typeof value !== 'object' || typeof other !== 'object') {
    return false;
  }

  if (Array.isArray(value) && Array.isArray(other)) {
    if (value.length !== other.length) {
      return false;
    }

    return value.every((item, index) => isEqual(item, other[index]));
  }

  const keysA = Object.keys(value);
  const keysB = Object.keys(other);

  if (keysA.length !== keysB.length) {
    return false;
  }

  for (const key of keysA) {
    if (!isEqual(value[key], other[key])) {
      return false;
    }
  }

  return true;
}

// 格式化后端数据
export function formatData<T extends { [key: string]: any } = {}>(
  data: DeepCamelToSnake<T>,
  options: {
    modifier?: (value: (T | any), key?: string) => (T | any);
  } = {},
): T {
  const {
    modifier = (value) => value,
  } = options;
  const formatTransferKey = (value: T | any, k?: string): any => {
    if (value instanceof Array) {
      return value.map(item => formatTransferKey(item));
    } else if (value instanceof Object) {
      const newValue: any = {};
      for (const key in value) {
        const keySnakeOrCamel = transferSnakeToCamel(key);
        newValue[keySnakeOrCamel] = formatTransferKey(value[key], key);
      }
      return newValue;
    }
    else if (typeof value === 'string') {
      return modifier(transferSnakeToCamel(value), k);
    } else if (typeof value === 'number') {
      // ID 如果是 number，序列化为 string
      const isId = k && /ids?$/i.test(k);
      return modifier(isId ? value.toString() : value, k);
    }
    return modifier(value, k);
  };

  return formatTransferKey(data);
}


export const evaluateExpression = (expression: string, context: any): boolean => {
  try {
    const fn = new Function('$', `return ${expression}`);
    const boundFn = fn.bind(null, context);
    return boundFn();
  } catch (error) {
    console.error("Error evaluating expression:", expression, error);
    return false;
  }
};


type CharacterCounts = {
  chinese: number;
  english: number;
};

const ENGLISH_NUMBER_TOKEN_UNIT_SIZE = 15;
const ENGLISH_NUMBER_CHAR_REGEX = /^[a-zA-Z0-9]$/;
const WHITESPACE_REGEX = /^\s$/;

export function countCharacters(text: string): CharacterCounts {
  let nonEnglishCount = 0;
  let englishNumberUnits = 0;
  let currentEnglishNumberTokenLength = 0;

  const flushEnglishNumberToken = () => {
    if (!currentEnglishNumberTokenLength) return;
    englishNumberUnits += Math.max(
      1,
      Math.ceil(currentEnglishNumberTokenLength / ENGLISH_NUMBER_TOKEN_UNIT_SIZE),
    );
    currentEnglishNumberTokenLength = 0;
  };

  Array.from(text || '').forEach((char) => {
    if (ENGLISH_NUMBER_CHAR_REGEX.test(char)) {
      currentEnglishNumberTokenLength += 1;
      return;
    }

    flushEnglishNumberToken();

    if (WHITESPACE_REGEX.test(char)) {
      return;
    }

    nonEnglishCount += 1;
  });

  flushEnglishNumberToken();

  return { chinese: nonEnglishCount, english: englishNumberUnits };
}

export function isExceedingLimits(text: string, maxLength: number): boolean {
  const counts = countCharacters(text);
  return counts.chinese + counts.english > maxLength;
}

export function truncateText(text: string, maxLength: number): string {
  let currentLength = countCharacters(text);
  let truncatedText = text;

  // 如果当前文本已经超出最大长度，从后往前截断直到满足条件
  while (currentLength.chinese + currentLength.english > maxLength && truncatedText.length > 0) {
    // 从文本末尾移除一个 Unicode 字符
    truncatedText = Array.from(truncatedText).slice(0, -1).join('');
    // 更新字符计数
    currentLength = countCharacters(truncatedText);
  }

  return truncatedText;
}
